/* FP journey timing + GoKwik warm-up (P1, 2026-07-13).
 *
 * Real-device timing beacons for the ATC → checkout journey, plus a checkout
 * warm-up on first drawer-open. Loaded site-wide from layout/theme.liquid
 * (defer, right before the fp-analytics render — the inline fp-analytics
 * snippet runs at parse time, so window.fpTrack always exists by the time
 * this evaluates; fpTrack fans out to gtag directly + Clarity, no GTM here).
 *
 * DESIGN CONSTRAINTS (from the 2026-07-13 hook-point recon — do not relax):
 * - Read-only observers. No DOM writes inside the drawer subtree (strip-bug
 *   history), no preventDefault/stopPropagation on money-path clicks, nothing
 *   async inserted before onCheckoutClick. Every handler body is try/catch'd.
 * - ATC/qty timing rides Horizon's OWN instrumentation: product-form.js and
 *   component-cart-items.js emit performance.measure entries named
 *   "cart-performance:<op>:user-action" (assets/performance.js) covering
 *   tap → fetch settled. A buffered PerformanceObserver reads them with zero
 *   edits to any money-path file.
 * - Checkout readiness signals are GoKwik's own postMessages: {type:
 *   'merchantInfo'} = modal open kick-off, gokwik_events Started_Checkout_GK
 *   = checkout UI interactive (same signals fp-analytics maps to
 *   fp_gokwik_open / fp_gokwik_started — we add the latency dimension).
 * - fp_-prefixed names only; never GA4-reserved names (GoKwik forwards those
 *   natively through the same gtag — double-count risk).
 */
(function () {
  'use strict';
  if (window.__fpJourneyTiming) return;
  window.__fpJourneyTiming = true;

  var MAX_MS = 60000; /* beyond this it's a suspended tab, not a measurement */

  function sane(v) {
    var n = Math.round(v);
    return n >= 0 && n <= MAX_MS ? n : null;
  }

  function send(name, params) {
    try {
      if (typeof window.fpTrack === 'function') window.fpTrack(name, params);
    } catch (e) { /* analytics must never break the page */ }
  }

  function connType() {
    try { return (navigator.connection || {}).effectiveType || 'unknown'; } catch (e) { return 'unknown'; }
  }

  /* ---- 1. Cart-op round-trips from Horizon's own performance.measure ---- */

  var MEASURE_MAP = {
    'cart-performance:add:user-action': 'fp_atc_confirm_ms',     /* PDP ATC tap → /cart/add.js settled */
    'cart-performance:change:user-action': 'fp_qty_change_ms',   /* qty +/- → /cart/change.js settled */
    'cart-performance:clear:user-action': 'fp_line_remove_ms'    /* line remove → settled */
  };

  try {
    var po = new PerformanceObserver(function (list) {
      try {
        list.getEntries().forEach(function (entry) {
          var eventName = MEASURE_MAP[entry.name];
          if (!eventName) return;
          var d = sane(entry.duration);
          if (d === null) return;
          send(eventName, { fp_ms: d, fp_connection: connType() });
        });
      } catch (e) {}
    });
    /* buffered: also report measures that completed before this script ran */
    po.observe({ type: 'measure', buffered: true });
  } catch (e) { /* old browser without measure observation — skip silently */ }

  /* ---- 2. PLACE ORDER click → GoKwik open/interactive latency ---- */

  var clickT0 = 0;
  var openSent = false;
  var readySent = false;

  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.fp-place-order, #gokwik-buy-now')) {
        clickT0 = performance.now(); /* latest attempt wins; every retry is a real attempt */
        openSent = false;
        readySent = false;
      }
    } catch (err) {}
  }, true);

  window.addEventListener('message', function (event) {
    try {
      var d = event.data;
      if (!d || !clickT0) return;
      if (d.type === 'merchantInfo' && !openSent) {
        openSent = true;
        var vOpen = sane(performance.now() - clickT0);
        if (vOpen !== null) send('fp_checkout_open_ms', { fp_ms: vOpen, fp_connection: connType() });
      } else if (d.type === 'gokwik_events' && d.data && d.data.eventname === 'Started_Checkout_GK' && !readySent) {
        readySent = true;
        var vReady = sane(performance.now() - clickT0);
        if (vReady !== null) {
          send('fp_checkout_ready_ms', {
            fp_ms: vReady,
            fp_connection: connType(),
            fp_gokwik_ready: document.body.classList.contains('fp-gokwik-ready')
          });
        }
      }
    } catch (err) {}
  }, false);

  /* ---- 3. GoKwik warm-up on first drawer-open (buy-intent signal) ----
   * The checkout iframe is created only on the first PLACE ORDER click, and
   * its two cold costs are (a) the TLS handshake + health check round-trip to
   * api-gw.gokwik.io and (b) fetching https://pdp.gokwik.co/index.html plus
   * its JS/CSS chain into the iframe. Opening the drawer is the strongest
   * pre-checkout intent signal we have, so warm both then. The page-load
   * preconnect to pdp.gokwik.co (theme.liquid head) is long dead by
   * checkout time — idle connections close within ~30s. */

  var warmed = false;

  function warmGokwik() {
    /* Kill switch: never open connections to a checkout we are not using. */
    if (window.FP_CHECKOUT_MODE === 'native') { warmed = true; return; }
    if (warmed) return;
    warmed = true;
    try {
      var pre = document.createElement('link');
      pre.rel = 'preconnect';
      pre.href = 'https://api-gw.gokwik.io';
      pre.crossOrigin = 'anonymous'; /* health check is a CORS request */
      document.head.appendChild(pre);

      var pf = document.createElement('link');
      pf.rel = 'prefetch';
      pf.href = 'https://pdp.gokwik.co/index.html';
      pf.as = 'document'; /* the iframe navigation target */
      document.head.appendChild(pf);

      send('fp_gk_warmup', { fp_trigger: 'drawer-open', fp_connection: connType() });
    } catch (e) {}
  }

  function wireWarmup() {
    if (window.FP_CHECKOUT_MODE === 'native') return;
    try {
      var comp = document.querySelector('cart-drawer-component');
      if (!comp) return; /* /cart page or drawer disabled — nothing to warm from */
      /* dialog:open covers the native showDialog() path… */
      comp.addEventListener('dialog:open', warmGokwik);
      /* …but the optimistic path can call bare dialog.showModal() (scroll-lock
         case) which never fires it, so watch [open] directly. The <dialog>
         element itself survives hydration morphs (only the inner subtree is
         replaced), so this observer node stays valid for the page's life. */
      var dlg = comp.querySelector('dialog');
      if (!dlg) return;
      if (dlg.hasAttribute('open')) return warmGokwik();
      var mo = new MutationObserver(function () {
        if (dlg.hasAttribute('open')) {
          warmGokwik();
          mo.disconnect();
        }
      });
      mo.observe(dlg, { attributes: true, attributeFilter: ['open'] });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireWarmup);
  } else {
    wireWarmup();
  }
})();
